import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/terms.scss";

function Terms() {
  const { brandName, legalName, currency, currencySymbol, contact } = siteConfig;
  const sections = [
    ["terms-scope", "Scope"],
    ["terms-account", "Using the store"],
    ["terms-products", "Products & pricing"],
    ["terms-orders", "Orders & payment"],
    ["terms-delivery", "Delivery"],
    ["terms-returns", "Returns"],
    ["terms-use", "Acceptable use"],
    ["terms-liability", "Responsibility"],
    ["terms-contact", "Changes & contact"],
  ];

  return (
    <main className="terms" id="main-content" tabIndex="-1">
      <header className="terms__header">
        <p>Legal / 02</p>
        <h1>Terms &amp;<br />conditions</h1>
        <div>
          <span>Version</span>
          <time dateTime="2026-08-14">14 August 2026</time>
        </div>
      </header>

      <aside className="terms__demo-note" role="note">
        <strong>Demonstration notice</strong>
        <p>
          These original terms are non-binding sample content for this front-end
          demonstration. They are not legal advice. Production terms must be
          reviewed for the actual seller, checkout, fulfilment and jurisdictions.
        </p>
      </aside>

      <div className="terms__layout">
        <nav className="terms__nav" aria-label="Terms and conditions sections">
          <p>On this page</p>
          <ol>
            {sections.map(([id, label], index) => (
              <li key={id}>
                <a href={`#${id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="terms__article">
          <section id="terms-scope">
            <p className="terms__section-number">01 / Scope</p>
            <h2>The agreement behind the experience.</h2>
            <p>
              These sample terms describe the intended relationship between a
              customer and {legalName}, trading as {brandName}, when using this
              website or placing an order. A live store must publish terms that
              identify the real seller and mandatory consumer rights in every
              market it serves.
            </p>
            <p>
              By using a production version of the store, a customer would agree
              to the terms available at that time. If a mandatory local rule
              conflicts with a clause, that rule takes priority.
            </p>
          </section>

          <section id="terms-account">
            <p className="terms__section-number">02 / Using the store</p>
            <h2>Accurate details, secure access.</h2>
            <p>
              You should provide complete, current information and be legally
              able to enter a purchase contract. If an account feature is added,
              you are responsible for keeping its credentials confidential and
              for telling us promptly about suspected unauthorised access.
            </p>
            <p>
              We may limit or suspend access where reasonably necessary to
              protect customers, investigate misuse, perform maintenance or meet
              a legal obligation. The store should remain available without an
              account wherever guest checkout is offered.
            </p>
          </section>

          <section id="terms-products">
            <p className="terms__section-number">03 / Products &amp; pricing</p>
            <h2>Considered products, clearly presented.</h2>
            <p>
              We aim to present colour, texture, dimensions, composition and fit
              accurately. Screen settings and the natural character of materials
              can create small differences. Product photography is illustrative;
              the written description and order summary define the selection.
            </p>
            <p>
              Prices are displayed in {currency} ({currencySymbol}) unless the
              interface states otherwise. A live checkout must show applicable
              taxes, delivery fees and the total before payment. Obvious pricing
              or inventory errors may be corrected before an order is accepted.
            </p>
          </section>

          <section id="terms-orders">
            <p className="terms__section-number">04 / Orders &amp; payment</p>
            <h2>From selection to confirmation.</h2>
            <ol className="terms__process">
              <li>
                <span>01</span>
                <div>
                  <h3>Place</h3>
                  <p>Review the cart, delivery details and total, then submit.</p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <h3>Authorise</h3>
                  <p>A connected provider securely confirms payment status.</p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <h3>Accept</h3>
                  <p>A dispatch confirmation forms the purchase contract.</p>
                </div>
              </li>
            </ol>
            <p>
              An automated acknowledgement confirms only that an order request
              was received. We may decline an order for unavailable stock,
              suspected fraud, delivery restrictions or an obvious error and
              should promptly release or refund any affected payment.
            </p>
          </section>

          <section id="terms-delivery">
            <p className="terms__section-number">05 / Delivery</p>
            <h2>Tracked from studio to destination.</h2>
            <p>
              Available services, estimated timing and cost are shown at
              checkout. Estimates can be affected by customs, weather, carrier
              capacity or events beyond reasonable control. You are responsible
              for supplying a complete, accessible delivery address.
            </p>
            <p>
              Risk and ownership should transfer only as defined by the final
              production terms and applicable consumer law. For the demonstration
              shipping experience, see <Link to="/shipping">shipping information</Link>.
            </p>
          </section>

          <section id="terms-returns">
            <p className="terms__section-number">06 / Returns</p>
            <h2>Room to reconsider.</h2>
            <p>
              Eligible items may be returned in their original condition within
              the stated return window. Exclusions, regional costs, exchange
              availability and refund timing should be disclosed before purchase
              and in the live <Link to="/returns">returns policy</Link>.
            </p>
            <p>
              Nothing in these sample terms limits rights that cannot legally be
              limited, including remedies for goods that are faulty, misdescribed
              or otherwise non-conforming.
            </p>
          </section>

          <section id="terms-use">
            <p className="terms__section-number">07 / Acceptable use</p>
            <h2>Use the experience as intended.</h2>
            <p>You must not:</p>
            <ul>
              <li>interfere with the security, availability or operation of the store;</li>
              <li>use automated means to overload, scrape or manipulate the service;</li>
              <li>submit unlawful, deceptive, harmful or infringing material;</li>
              <li>attempt an unauthorised purchase or access another person’s data;</li>
              <li>reuse protected site content beyond what law or permission allows.</li>
            </ul>
          </section>

          <section id="terms-liability">
            <p className="terms__section-number">08 / Responsibility</p>
            <h2>A fair allocation of responsibility.</h2>
            <p>
              A production store should be responsible for losses that are a
              foreseeable result of its breach or negligence, subject to rights
              and limitations allowed by local law. It should not exclude
              responsibility where doing so would be unlawful.
            </p>
            <p>
              The front-end demonstration is provided without a live payment,
              inventory, fulfilment or return service. Actions completed here do
              not create a real order or transaction.
            </p>
          </section>

          <section id="terms-contact">
            <p className="terms__section-number">09 / Changes &amp; contact</p>
            <h2>Questions about these terms?</h2>
            <p>
              Material changes should be dated and, where appropriate, brought
              to customers’ attention. Terms applying to an accepted order should
              not be changed retrospectively.
            </p>
            <p>
              Email <a href={`mailto:${contact.email}`}>{contact.email}</a> or
              review our <Link to="/privacy">privacy policy</Link> for information
              about personal data.
            </p>
            <address>
              {legalName}<br />
              {contact.address}
            </address>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Terms;
